import { useRef, useState } from 'react';
import { Modal } from '@/components/dialogs/Modal';

interface Props {
  show: boolean;
  providerTitle: string;
  busy?: boolean;
  onConfirm: (file: File, comment: string) => void;
  onCancel: () => void;
}

const ACCEPT = '.csv, .xlsx, .xls';

// Replaces the legacy file-input + SweetAlert note prompt: a single modal taking the
// file AND a mandatory note ("A comment is required."). Mirrors ps-toolbar.js upload.
export function UploadExcelDialog({ show, providerTitle, busy = false, onConfirm, onCancel }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const commentInvalid = comment.trim().length === 0;
  const fileInvalid = !file;

  function reset() {
    setFile(null);
    setComment('');
    setTouched(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  function handleConfirm() {
    if (fileInvalid || commentInvalid) {
      setTouched(true);
      return;
    }
    onConfirm(file as File, comment.trim());
    reset();
  }

  function handleCancel() {
    reset();
    onCancel();
  }

  return (
    <Modal
      show={show}
      title="Upload Statement"
      onClose={handleCancel}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={handleCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={busy || fileInvalid || commentInvalid}
          >
            {busy ? (
              <>
                <i className="fa fa-spinner fa-spin me-1" />
                Working…
              </>
            ) : (
              'Upload'
            )}
          </button>
        </>
      }
    >
      <div className="mb-3">
        <label className="form-label">File</label>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className={`form-control${touched && fileInvalid ? ' is-invalid' : ''}`}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {touched && fileInvalid && (
          <div className="invalid-feedback d-block">Please select a file before uploading.</div>
        )}
      </div>
      <p className="mb-2">Add a note for future reference in relation to this upload</p>
      <textarea
        className={`form-control${touched && commentInvalid ? ' is-invalid' : ''}`}
        rows={3}
        placeholder={`${providerTitle} Statement Period January 2023`}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      {touched && commentInvalid && <div className="invalid-feedback d-block">A comment is required.</div>}
    </Modal>
  );
}
